import React, { useMemo } from "react";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { backend_url } from "../../server";
import { computeBundleSelectionPricing } from "../../utils/bundlePricing";

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "£0.00";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value));
};

const BundleReviewModal = ({
  products = [],
  bundleRules = [],
  bundlePostageConfig,
  buyerProtectionConfig,
  onBack,
  onClose,
  onBuyNow,
  onAskSeller,
  onMakeOffer,
  isOwnProduct = false,
}) => {
  const pricing = useMemo(
    () =>
      computeBundleSelectionPricing({
        products,
        bundleRules,
        bundlePostageConfig,
        buyerProtectionConfig,
      }),
    [products, bundleRules, bundlePostageConfig, buyerProtectionConfig]
  );

  const canMakeOffer = products.length >= 2 && !isOwnProduct;

  return (
    <div className="va-modal fixed inset-0 z-[1000] flex items-end justify-center overflow-y-auto overscroll-contain bg-black/50 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-xl sm:rounded-2xl">
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 px-4 py-4">
          <button
            type="button"
            onClick={onBack}
            className="flex h-10 w-10 items-center justify-center rounded-full text-slate-700 hover:bg-slate-100"
            aria-label="Back to bundle selection"
          >
            <AiOutlineArrowLeft size={22} />
          </button>
          <h2 className="flex-1 text-center text-lg font-semibold text-slate-900 pr-10">
            Review bundle
          </h2>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5">
          <div className="flex items-center justify-between gap-4">
            <p className="text-base font-semibold text-slate-900">
              {pricing.itemCount} {pricing.itemCount === 1 ? "Item" : "Items"}
            </p>
            <div className="flex shrink-0 gap-1.5">
              {products.map((product) => {
                const imageSrc =
                  product?.images?.length > 0
                    ? `${backend_url}${product.images[0]}`
                    : null;
                return (
                  <div
                    key={product._id}
                    className="h-14 w-14 overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
                  >
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={product.name || "Product"}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">
                        No image
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 space-y-3 border-t border-slate-200 pt-5 text-sm">
            <div className="flex items-center justify-between text-slate-700">
              <span>Price</span>
              <span className="font-medium text-slate-900">{formatCurrency(pricing.subtotal)}</span>
            </div>

            {pricing.bundleDiscount > 0 ? (
              <div className="flex items-center justify-between text-slate-700">
                <span>
                  Bundle discount
                  {pricing.discountPercent > 0 ? ` (${pricing.discountPercent}%)` : ""}
                </span>
                <span className="font-medium text-emerald-700">
                  -{formatCurrency(pricing.bundleDiscount)}
                </span>
              </div>
            ) : null}

            {pricing.buyerProtectionFee > 0 ? (
              <div className="flex items-center justify-between text-slate-700">
                <span className="inline-flex items-center gap-1">
                  Buyer Protection fee
                  <span
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 text-[10px] font-bold text-slate-500"
                    title="Helps cover your purchase if something goes wrong with the order."
                  >
                    i
                  </span>
                </span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(pricing.buyerProtectionFee)}
                </span>
              </div>
            ) : null}

            {pricing.postageCharged > 0 || pricing.postageTotal > 0 ? (
              <div className="flex items-center justify-between text-slate-700">
                <span>Postage</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(pricing.postageCharged)}
                </span>
              </div>
            ) : null}

            {pricing.postageDiscount > 0 ? (
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Bundle postage saving included above</span>
                <span>-{formatCurrency(pricing.postageDiscount)}</span>
              </div>
            ) : null}

            <div className="flex items-center justify-between border-t border-slate-200 pt-4 text-base">
              <span className="font-semibold text-slate-900">Total</span>
              <span className="text-lg font-bold text-slate-900">
                {formatCurrency(pricing.total)}
              </span>
            </div>
          </div>

          <ul className="mt-4 space-y-2">
            {products.map((product) => (
              <li
                key={product._id}
                className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
              >
                <span className="line-clamp-1 flex-1 text-xs text-slate-700">{product.name}</span>
                <span className="shrink-0 text-xs font-semibold text-slate-900">
                  {formatCurrency(Number(product.discountPrice || 0))}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="shrink-0 space-y-3 border-t border-slate-200 bg-white px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {!isOwnProduct ? (
            <button
              type="button"
              onClick={onBuyNow}
              className="flex h-12 w-full items-center justify-center rounded-xl bg-[#38513b] text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232]"
            >
              Buy now
            </button>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onAskSeller}
              disabled={isOwnProduct}
              className="flex h-11 items-center justify-center rounded-xl border border-[#38513b] bg-white text-sm font-semibold text-[#38513b] transition hover:bg-[#38513b]/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Ask seller
            </button>
            <button
              type="button"
              onClick={onMakeOffer}
              disabled={!canMakeOffer}
              className="flex h-11 items-center justify-center rounded-xl border border-[#38513b] bg-white text-sm font-semibold text-[#38513b] transition hover:bg-[#38513b]/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Make an offer
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-full text-center text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BundleReviewModal;
